import { useCallback, useEffect, useState } from "react";

import { Toggle } from "@magnetic/toggle";

import { HFlex } from "src/components";
import { ErrorNotification } from "src/components/errorNotification";
import { Text } from "src/components/text";
import { VFlex } from "src/components/vFlex";
import { ApiError } from "src/core/apiError";
import { Bilge } from "src/gen/rest/bilge/BilgeService";
import {
  ChatbotFeatureFlagsResponse,
  ChatbotFeatureFlagState,
  UpdateChatbotFeatureFlagsRequest
} from "src/gen/schema/bilge/api";

/**
 * Toggle component for enabling/disabling multi-step tool calling
 * to improve bot accuracy. Makes bilge API calls to get/update feature flags.
 */
export const ImproveBotAccuracyToggle = () => {
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<ApiError | undefined>();

  const fetchFeatureFlags = useCallback(() => {
    setLoading(true);
    setError(undefined);

    Bilge.getChatbotFeatureFlags()
      .then((response: ChatbotFeatureFlagsResponse) => {
        const multiStepEnabled =
          response.featureFlags?.multiStepToolCalling ===
          ChatbotFeatureFlagState.CHATBOT_FEATURE_FLAG_STATE_ENABLED;
        setIsEnabled(multiStepEnabled);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  const handleToggle = useCallback(() => {
    if (loading) return;

    setLoading(true);
    setError(undefined);

    const nextIsEnabled = !isEnabled;
    const newState = nextIsEnabled
      ? ChatbotFeatureFlagState.CHATBOT_FEATURE_FLAG_STATE_ENABLED
      : ChatbotFeatureFlagState.CHATBOT_FEATURE_FLAG_STATE_DISABLED;

    const request = UpdateChatbotFeatureFlagsRequest.create({
      featureFlags: {
        multiStepToolCalling: newState
      }
    });

    Bilge.updateChatbotFeatureFlags(request)
      .then(() => {
        setIsEnabled(nextIsEnabled);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [isEnabled, loading]);

  return (
    <VFlex gap="sm">
      <ErrorNotification error={error} />
      <HFlex gap="sm" align="flex-start">
        <Toggle
          checked={isEnabled}
          onClick={handleToggle}
          disabled={loading}
          data-qa="improve-bot-accuracy-toggle"
        />
        <VFlex gap="xs">
          <Text weight="semi-bold">Improve bot accuracy</Text>
          <Text>
            Allows Hyperfabricbot to solve complex tasks, which may involve
            sharing your data with our AI service.
          </Text>
        </VFlex>
      </HFlex>
    </VFlex>
  );
};
